import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, ExternalLink, ChevronDown, ChevronRight, Eye } from "lucide-react";
import api from "./api";
import { refreshSiteContent } from "../store/useSiteContent";
import ImageUploader from "../components/ImageUploader";
import { HomeAnnouncementsEditor, HomeGalleryEditor, TimelineEditor } from "./HomePageEditors";

/**
 * Editable content schema. Each section groups related keys.
 * `kind`: "image" | "text" | "longtext" | "advanced"
 * `fallback` is only shown as helper hint to admin.
 */
const SCHEMA = [
  {
    section: "Landing page",
    description: "The very first page anyone sees , the splash with the Heritage tagline.",
    preview: "/",
    fields: [
      { key: "start.tagline", kind: "longtext", label: "Sub-tagline under HERITAGE", placeholder: "One family. Many stories. Where would you like to wander?" },
      { key: "start.businesses", kind: "advanced", label: "Business tiles (advanced, JSON)",
        placeholder: '[ {"slug":"apparel","label":"Apparel","number":"01","to":"/apparel","tone":"dark","badge":"Coming soon","headline":"Heritage Apparel","blurb":"Coming soon. We\'re changing things up a bit.","cta":"View Heritage Apparel","image":"/photos/apparel-rack.jpg"}, {"slug":"dahlias","label":"Dahlias","number":"02","to":"/dahlias","tone":"light","badge":"Family farm · Est. 1932","headline":"Heritage Dahlias","blurb":"Tubers, workshops & the story behind the farm.","cta":"Enter Heritage Dahlias","image":"/photos/dahlia-field.jpg"} ]',
        helper: "Only touch this if you want to add or rearrange the tiles on the landing page. Leave blank for the default layout.",
      },
    ],
  },
  {
    section: "Apparel page",
    description: "Holding page for Heritage Apparel while it's still in development.",
    preview: "/apparel",
    fields: [
      { key: "apparel.hero.image", kind: "image", label: "Hero photo" },
      { key: "apparel.hero.headline", kind: "text", label: "Headline (use *…* for italic accent)", placeholder: "Releasing *Spring 2026.*" },
      { key: "apparel.hero.body", kind: "longtext", label: "Body copy" },
    ],
  },
  {
    section: "Dahlias , Home",
    description: "The Heritage Dahlias homepage hero + welcome paragraph.",
    preview: "/dahlias",
    fields: [
      { key: "dahlias.home.hero", kind: "image", label: "Hero banner photo" },
      { key: "dahlias.home.hero.logo", kind: "image", label: "Hero logo (simple outline, shown big under the headline)" },
      { key: "dahlias.home.hero.subtitle", kind: "text", label: "Hero subtitle (under HERITAGE DAHLIAS)", placeholder: "Growing exceptional dahlias since 1932." },
      { key: "dahlias.home.welcome.heading", kind: "text", label: "Welcome heading (italic part in *…*)", placeholder: "Welcome to *Heritage Dahlias.*" },
      { key: "dahlias.home.welcome.body", kind: "longtext", label: "Welcome body copy" },
    ],
  },
  {
    section: "About & Contact",
    description: "The story of Nana Jean, Mum's Shed, and the family page.",
    preview: "/dahlias/about",
    fields: [
      { key: "about.nana_jean.image", kind: "image", label: "Nana Jean portrait" },
      { key: "about.mums_shed.image", kind: "image", label: "Mum's Shed photo" },
      { key: "about.heading", kind: "text", label: "Heading (italic part in *…*)", placeholder: "Carrying on *Nana Jean's legacy.*" },
      { key: "about.body", kind: "longtext", label: "Story body copy" },
    ],
  },
  {
    section: "Farm Shop",
    description: "The shop page header, plus the 'coming soon' message shown when the shop is empty.",
    preview: "/dahlias/store",
    fields: [
      { key: "shop.eyebrow", kind: "text", label: "Small label above heading", placeholder: "✦ THE FARM SHOP ✦" },
      { key: "shop.heading", kind: "text", label: "Page heading (italic part in *…*)", placeholder: "Shop *Heritage.*" },
      { key: "shop.empty.heading", kind: "text", label: "Empty-shop heading (italic part in *…*)", placeholder: "The shop is *resting.*" },
      { key: "shop.empty.body", kind: "longtext", label: "Empty-shop message", placeholder: "Tubers, workshops and a few small things will land here when the season turns…" },
    ],
  },
  {
    section: "Catalogue",
    description: "The 2026 catalogue header, seal image and the 'note before you browse' box.",
    preview: "/dahlias/catalog",
    fields: [
      { key: "catalog.seal.image", kind: "image", label: "Heritage seal image" },
      { key: "catalog.heading", kind: "text", label: "Page heading", placeholder: "HERITAGE DAHLIAS NZ" },
      { key: "catalog.subtitle", kind: "text", label: "Subtitle under heading", placeholder: "The 2026 catalogue." },
      { key: "catalog.notice.1", kind: "longtext", label: "Notice paragraph 1", placeholder: "This catalogue is here to show our collection. Tuber sales open in September…" },
      { key: "catalog.notice.2", kind: "longtext", label: "Notice paragraph 2", placeholder: "Mystery Boxes go on sale 1 July…" },
      { key: "catalog.notice.3", kind: "longtext", label: "Notice paragraph 3 (italic)", placeholder: "If you have specific questions or wishes don't hesitate to contact us directly." },
    ],
  },
  {
    section: "Workshops",
    description: "The workshops page hero, the three workshop cards, and the green 'we'll write to you first' band.",
    preview: "/dahlias/workshops",
    fields: [
      { key: "workshops.hero.image", kind: "image", label: "Hero background pattern/photo" },
      { key: "workshops.heading", kind: "text", label: "Heading (italic part in *…*, new line where you want the break)", placeholder: "Winter & spring\n*on the farm.*" },
      { key: "workshops.intro", kind: "longtext", label: "Intro paragraph", placeholder: "Small groups, on the farm, in the seasons we love best…" },
      { key: "workshops.card1.title", kind: "text", label: "Card 1 · title", placeholder: "Tuber Dividing" },
      { key: "workshops.card1.blurb", kind: "longtext", label: "Card 1 · description", placeholder: "Hands-on, in the shed…" },
      { key: "workshops.card2.title", kind: "text", label: "Card 2 · title", placeholder: "Picking & Arranging" },
      { key: "workshops.card2.blurb", kind: "longtext", label: "Card 2 · description", placeholder: "An afternoon in the rows…" },
      { key: "workshops.card3.title", kind: "text", label: "Card 3 · title", placeholder: "Open Day & Tour" },
      { key: "workshops.card3.blurb", kind: "longtext", label: "Card 3 · description", placeholder: "A walk through the farm with morning tea…" },
      { key: "workshops.band.heading", kind: "text", label: "Green band heading (italic part in *…*)", placeholder: "We'll write to *you first.*" },
      { key: "workshops.band.body", kind: "longtext", label: "Green band body", placeholder: "Numbers are small and word travels fast…" },
    ],
  },
  {
    section: "Mystery Boxes",
    description: "The public mystery box page , berry banner and intro copy.",
    preview: "/dahlias/mystery-boxes",
    fields: [
      { key: "mystery.banner.heading", kind: "text", label: "Banner heading", placeholder: "Mystery Boxes Are Back" },
      { key: "mystery.banner.body", kind: "longtext", label: "Banner body", placeholder: "A chance to grab an absolute steal from the Heritage Dahlias collection…" },
      { key: "mystery.intro.heading", kind: "text", label: "Intro heading", placeholder: "Heritage Mystery Boxes" },
      { key: "mystery.intro.body", kind: "longtext", label: "Intro paragraphs (blank line = new paragraph)", placeholder: "Our Heritage Mystery Boxes are packed from across the collection…" },
    ],
  },
  {
    section: "Heritage Garden",
    description: "The Breeders' Bed page header and intro. Note: this page is currently hidden from the public site , your edits are saved and will appear when the page is switched on.",
    preview: "/heritage-garden",
    fields: [
      { key: "garden.heading", kind: "text", label: "Heading (italic part in *…*)", placeholder: "The Heritage Garden *Breeders' Bed.*" },
      { key: "garden.intro", kind: "longtext", label: "Intro paragraph", placeholder: "A small bed at our family farm given over to breeders growing something new…" },
    ],
  },
];

const ALL_KEYS = SCHEMA.flatMap((s) => s.fields.map((f) => f.key));

export default function ConsoleSiteContent() {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/site-content");
      const data = res.data || {};
      const next = {};
      for (const k of ALL_KEYS) next[k] = data[k] || "";
      setValues(next);
    } catch {
      toast.error("Couldn't load site content");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setVal = (key, v) => setValues((cur) => ({ ...cur, [key]: v }));

  const saveOne = async (key) => {
    try {
      setSavingKey(key);
      await api.put(`/admin/site-content/${encodeURIComponent(key)}`, {
        value: values[key] || "",
      });
      await refreshSiteContent();
      toast.success("Saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSavingKey(null);
    }
  };

  const renderField = (f) => {
    const v = values[f.key] ?? "";
    if (f.kind === "image") {
      return (
        <ImageUploader
          value={v}
          onChange={(url) => setVal(f.key, url)}
          label={f.label}
          testid={`uploader-${f.key.replace(/\./g, "-")}`}
        />
      );
    }
    if (f.kind === "longtext") {
      return (
        <div>
          <label className="block font-display tracking-[0.3em] text-xs uppercase text-[var(--dahlias-clay)] mb-2">
            {f.label}
          </label>
          <textarea
            data-testid={`field-${f.key.replace(/\./g, "-")}`}
            rows={4}
            value={v}
            onChange={(e) => setVal(f.key, e.target.value)}
            placeholder={f.placeholder}
            className="w-full px-3 py-2 bg-[var(--dahlias-cream)] border border-[var(--dahlias-ink)]/15 text-base text-[var(--dahlias-ink)] rounded-sm"
          />
          {f.helper && <p className="mt-1 text-xs text-[var(--dahlias-ink)]/55">{f.helper}</p>}
        </div>
      );
    }
    return (
      <div>
        <label className="block font-display tracking-[0.3em] text-xs uppercase text-[var(--dahlias-clay)] mb-2">
          {f.label}
        </label>
        <input
          data-testid={`field-${f.key.replace(/\./g, "-")}`}
          type="text"
          value={v}
          onChange={(e) => setVal(f.key, e.target.value)}
          placeholder={f.placeholder}
          className="w-full h-12 px-3 bg-[var(--dahlias-cream)] border border-[var(--dahlias-ink)]/15 text-base text-[var(--dahlias-ink)] rounded-sm"
        />
      </div>
    );
  };

  return (
    <div data-testid="admin-site-content" className="space-y-8 max-w-4xl">
      <header>
        <span className="font-label tracking-[0.4em] text-[11px] text-[var(--dahlias-sage-deep)]">
          ✦ HERITAGE ADMIN ✦
        </span>
        <h1 className="font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] mt-2 leading-[1.05]">
          Edit website <span className="font-display-italic text-[var(--dahlias-sage-deep)]">content.</span>
        </h1>
        <p className="mt-3 text-base text-[var(--dahlias-ink)]/70 max-w-2xl leading-relaxed">
          Edit images and copy across the public site without touching code. Type into a field,
          press <strong>Save</strong> next to it, then click <strong>View live page →</strong> to see how it looks.
          Each field is independent , you can edit one thing at a time without breaking anything else.
        </p>
        <ul className="mt-3 text-xs text-[var(--dahlias-ink)]/60 list-disc pl-5 space-y-0.5">
          <li>Wrap a phrase in <code className="font-mono">*asterisks*</code> to make it italic on the page.</li>
          <li>Images: drag &amp; drop, paste, or click to browse. Recommended 1500px wide.</li>
          <li>&quot;Advanced&quot; fields are optional , safe to ignore unless you know what they do.</li>
        </ul>
      </header>

      {loading ? (
        <div className="flex items-center gap-3 text-[var(--dahlias-ink)]/60">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          <HomeAnnouncementsEditor />
          <HomeGalleryEditor />
          <TimelineEditor />
          {SCHEMA.map((section, sIdx) => (
            <ContentSection
              key={section.section}
              section={section}
              sIdx={sIdx}
              renderField={renderField}
              saveOne={saveOne}
              savingKey={savingKey}
            />
          ))}
        </>
      )}
    </div>
  );
}


function ContentSection({ section, sIdx, renderField, saveOne, savingKey }) {
  const radii = ["wobble-a", "wobble-b", "wobble-c"];
  const [showAdvanced, setShowAdvanced] = useState(false);
  const visibleFields    = section.fields.filter((f) => f.kind !== "advanced");
  const advancedFields   = section.fields.filter((f) => f.kind === "advanced");

  return (
    <section
      data-testid={`section-${section.section.replace(/[^a-z]/gi, "-").toLowerCase()}`}
      className={`bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/25 p-5 md:p-7 ${radii[sIdx % radii.length]}`}
    >
      {/* Section header */}
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="font-display text-xl md:text-2xl text-[var(--dahlias-forest)]">
          {section.section}
        </h2>
        {section.preview && (
          <a
            href={section.preview}
            target="_blank"
            rel="noreferrer"
            data-testid={`view-live-${section.section.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold text-[var(--dahlias-sage-deep)] hover:text-[var(--dahlias-forest)] whitespace-nowrap"
            title="Open this page in a new tab"
          >
            <Eye className="w-3 h-3" /> View live page →
          </a>
        )}
      </div>
      {section.description && (
        <p className="text-sm italic text-[var(--dahlias-ink)]/60 mb-5">{section.description}</p>
      )}

      <div className="space-y-6">
        {visibleFields.map((f) => (
          <FieldRow key={f.key} f={f} renderField={renderField} saveOne={saveOne} savingKey={savingKey} />
        ))}

        {advancedFields.length > 0 && (
          <div className="pt-3 border-t border-[var(--dahlias-sage)]/20">
            <button
              type="button"
              onClick={() => setShowAdvanced((s) => !s)}
              className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold text-[var(--dahlias-ink)]/55 hover:text-[var(--dahlias-forest)]"
              data-testid={`toggle-advanced-${section.section.replace(/[^a-z]/gi, "-").toLowerCase()}`}
            >
              {showAdvanced ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              Advanced fields ({advancedFields.length})
            </button>
            {showAdvanced && (
              <div className="mt-4 space-y-6">
                {advancedFields.map((f) => (
                  <FieldRow key={f.key} f={{ ...f, kind: "longtext" }} renderField={renderField} saveOne={saveOne} savingKey={savingKey} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}


function FieldRow({ f, renderField, saveOne, savingKey }) {
  return (
    <div className="space-y-3">
      {renderField(f)}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => saveOne(f.key)}
          disabled={savingKey === f.key}
          data-testid={`save-${f.key.replace(/\./g, "-")}`}
          className="inline-flex items-center gap-2 px-5 h-10 bg-[var(--dahlias-sage)] text-[var(--dahlias-cream)] text-sm hover:bg-[var(--dahlias-sage-deep)] transition disabled:opacity-50 wobble-pill"
        >
          {savingKey === f.key ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
        <span className="text-[10px] text-[var(--dahlias-ink)]/30 font-mono" title="Tech key , for support reference only">{f.key}</span>
      </div>
    </div>
  );
}
